import { useQuery, type QueryKey, type UseQueryOptions } from "react-query";
import {
  getBusinessUnits,
  getRegionsList,
  getSearchFileResults,
  getSearchResults,
  getStatusListForSearch
} from "../services/search.services";
import { fetchStatusesListConditionally } from "../utils/utils";
import type { SearchFileResults } from "../types/search.types";
import type { UseQueryReturnType } from "@certa/common/types";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useGetBusinessUnits = (region?: string | null) =>
  useQuery({
    retry: 0,
    staleTime: Infinity,
    queryKey: region ? ["businessUnits", region] : ["businessUnits"],

    queryFn: () => {
      if (!region) {
        return Promise.reject("region must be a string!");
      }
      return getBusinessUnits(region);
    }
  });

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useGetRegions = () =>
  useQuery({
    retry: 0,
    staleTime: Infinity,
    queryKey: "regions",
    queryFn: getRegionsList
  });

export const getStatusListForSearchQuery = () => ({
  retry: 0,
  staleTime: Infinity,
  queryKey: "search-statuses",
  queryFn: getStatusListForSearch
});

export const useGetStatusListForSearch = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getStatusListForSearch>>
) => useQuery({ ...getStatusListForSearchQuery(), ...config });

export const getSearchResultsQuery = (params: {
  urlParams: string;
  page: number;
}): UseQueryOptions<UseQueryReturnType<typeof getSearchResults>> => {
  return {
    retry: 0,
    // 1 minute
    staleTime: 60 * 1000,
    queryKey: ["search-results", params.urlParams, params.page] as QueryKey,
    queryFn: async ({ signal }) => {
      fetchStatusesListConditionally();
      return getSearchResults(params.urlParams, params.page, { signal });
    }
  };
};

export const useGetSearchResults = (
  params: {
    urlParams: string;
    page: number;
  },
  config?: UseQueryOptions<UseQueryReturnType<typeof getSearchResults>>
) =>
  useQuery({
    ...getSearchResultsQuery(params),
    ...config
  });

export const useGetSearchFileResults = (
  params: {
    searchText: string;
    page: number;
  },
  config?: UseQueryOptions<UseQueryReturnType<typeof getSearchFileResults>>
) => {
  return useQuery<any, any, SearchFileResults>({
    retry: 0,
    // 1 minute
    staleTime: 60 * 1000,
    ...config,
    queryKey: ["attachment-search", params.searchText, params.page] as QueryKey,
    queryFn: async ({ signal }) => {
      return getSearchFileResults(params.searchText, params.page, { signal });
    }
  });
};
